import { useState } from "react";
import { PRD4011_R01PlcStatusDto } from "./Dto_PRD4011_R01";

interface Props {
    alarmList: PRD4011_R01PlcStatusDto[];
    onConfirm: (plcStatus: PRD4011_R01PlcStatusDto) => Promise<void>;
}

/**
 * 에러 발생 알림 팝업
 * - 확인 버튼으로만 닫힘 (배경 클릭 / 자동조회로 닫히지 않음)
 * - 여러 건이면 앞에서부터 1건씩 표시
 */
export const ErrorAlarmModal_PRD4011_R01 = ({ alarmList, onConfirm }: Props) => {
    const [isConfirming, setIsConfirming] = useState(false);

    if (alarmList.length === 0) return null;
    const alarm = alarmList[0];

    const handleConfirmClick = async () => {
        if (isConfirming) return;
        setIsConfirming(true);
        try {
            await onConfirm(alarm);
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
            <div
                role="alertdialog"
                aria-labelledby="PRD4011_R01-alarm-title"
                className="w-[460px] bg-white border-2 border-[#c62828] rounded-[10px] overflow-hidden flex flex-col"
            >
                <div className="flex items-center justify-between px-[20px] py-[14px] bg-[#c62828] text-white">
                    <div className="flex items-center gap-[10px]">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 3 2 21h20L12 3z" />
                            <path d="M12 10v5" />
                            <path d="M12 18h.01" />
                        </svg>
                        <span id="PRD4011_R01-alarm-title" className="text-[19px] font-extrabold">
                            에러 발생 알림
                        </span>
                    </div>
                    {alarmList.length > 1 && (
                        <span className="px-[10px] py-[3px] rounded-full bg-[#8e1b1b] text-[13px] font-bold tabular-nums">
                            1 / {alarmList.length}
                        </span>
                    )}
                </div>

                <div className="flex gap-[18px] px-[24px] py-[22px]">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 7v6" />
                        <path d="M12 16.5h.01" />
                    </svg>
                    <div className="flex-1 grid grid-cols-[88px_minmax(0,1fr)] border-t border-[#e3e8f0] text-[15px]">
                        <div className="px-[12px] py-[10px] bg-[#f4f6fa] text-[#5a6577] border-b border-[#e3e8f0]">설비명</div>
                        <div className="px-[12px] py-[10px] font-extrabold border-b border-[#e3e8f0]">{alarm.equipNm}</div>
                        <div className="px-[12px] py-[10px] bg-[#f4f6fa] text-[#5a6577] border-b border-[#e3e8f0]">에러코드</div>
                        <div className="px-[12px] py-[10px] font-bold border-b border-[#e3e8f0] tabular-nums">{alarm.errorValue ?? "-"}</div>
                        <div className="px-[12px] py-[10px] bg-[#f4f6fa] text-[#5a6577] border-b border-[#e3e8f0]">에러내용</div>
                        <div className="px-[12px] py-[10px] font-bold text-[#a51d24] border-b border-[#e3e8f0]">{alarm.messageNm ?? "등록되지 않은 에러"}</div>
                        <div className="px-[12px] py-[10px] bg-[#f4f6fa] text-[#5a6577] border-b border-[#e3e8f0]">발생시간</div>
                        <div className="px-[12px] py-[10px] font-bold border-b border-[#e3e8f0] tabular-nums">{alarm.occurDt ?? "-"}</div>
                    </div>
                </div>

                <div className="flex justify-end px-[24px] pb-[20px]">
                    <button
                        type="button"
                        disabled={isConfirming}
                        onClick={() => {
                            void handleConfirmClick();
                        }}
                        className="h-[44px] px-[32px] rounded-md bg-[#c62828] text-white text-[16px] font-extrabold disabled:opacity-60"
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
};
